import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
    amountDisplay,
    dateDisplay,
    getMonthName,
    getUserTimezone,
    numberLanguageWise,
} from '../../util/helper';
import { useLanguage } from '../../context/LanguageContext';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import serverClient from '../../api/ServerClient';
import { DeleteToken } from '../../auth/Auth';
import feather from 'feather-icons';
import RegularHeader from '../../components/Common/RegularHeader';
import Alert from '../../components/Alert';
import Loader from '../../components/Loader';
import BottomNavbar from '../../components/Home/BottomNavbar';
import BackButton from '../../components/BackButton';
import DayWiseLineChart from '../../components/Charts/DayWiseLineChart';
import CategoryBarChart from '../../components/Charts/CategoryBarChart';
import { getPageMeta } from '../../util/seoHelper';

function ReportIncome() {
    dayjs.extend(utc);
    dayjs.extend(timezone);
    const timeZone = getUserTimezone();
    const { language, t } = useLanguage();

    const navigate = useNavigate();
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    const [customMonth, setCustomMonth] = useState('');
    const [data, setData] = useState(null);

    const [searchParams, setSearchParams] = useSearchParams();
    const month = searchParams.get('month');
    const initialReportingType = searchParams.get('type') === 'yearly' ? 'yearly' : 'monthly';
    const [dataMonth, setDataMonth] = useState(month || null);
    const [reportingType, setReportingType] = useState(initialReportingType);
    const isYearly = reportingType === 'yearly';
    const currentPeriodStart = dataMonth
        ? dayjs(dataMonth).tz(timeZone)
        : dayjs()
              .tz(timeZone)
              .startOf(isYearly ? 'year' : 'month');

    const syncSearchParams = (nextMonth, nextReportingType) => {
        setSearchParams({ type: nextReportingType, month: nextMonth });
    };

    const fetchReportIncomeData = useCallback(async () => {
        try {
            setLoading(true);
            let uri;

            if (dataMonth) {
                const normalizedPeriodStart = dayjs(dataMonth)
                    .tz(timeZone)
                    .startOf(isYearly ? 'year' : 'month')
                    .format('YYYY-MM-DD');
                uri = `/api/user/report/income?month=${normalizedPeriodStart}&type=${reportingType}`;
            } else {
                uri = `/api/user/report/income?type=${reportingType}`;
            }
            const response = await serverClient.get(`${uri}`);
            setData(response?.data?.data);
        } catch (e) {
            if (e.response?.status === 401) {
                DeleteToken();
                setError(t('userNotAuthorized'));
                navigate('/login');
            } else {
                setError(t('somethingWentWrong'));
            }
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataMonth, isYearly, reportingType, timeZone, t, navigate]);

    useEffect(() => {
        fetchReportIncomeData();
        feather.replace();
    }, [fetchReportIncomeData]);

    useEffect(() => {
        feather.replace();
    });

    const periodLabel = isYearly
        ? numberLanguageWise(currentPeriodStart.format('YYYY'), language)
        : dateDisplay(currentPeriodStart, language, 'monthOnly');
    const chartHeader = `${t('totalIncome')} | ${periodLabel}`;
    const yearChartLabels = useMemo(() => {
        if (!isYearly) {
            return null;
        }

        const labelCount = Math.max(0, Math.min(12, data?.incomeData?.length || 0));

        return Array.from({ length: labelCount }, (_, index) => {
            const monthCode = dayjs().month(index).format('MMM');
            return getMonthName(monthCode, t);
        });
    }, [data, isYearly, t]);

    if (loading || !data) {
        return (
            <>
                <RegularHeader headerTitle={t('incomeReport')} />
                <div className="custom-container p-4">
                    {error && <Alert alert={'danger'}>{error}</Alert>}

                    <Loader loading={loading} />
                    <BottomNavbar />
                </div>
            </>
        );
    }

    const handlePrevPeriod = (e) => {
        e.preventDefault();
        const previousPeriodStart = currentPeriodStart
            .subtract(1, isYearly ? 'year' : 'month')
            .startOf(isYearly ? 'year' : 'month')
            .format('YYYY-MM-DD');
        setDataMonth(previousPeriodStart);
        syncSearchParams(previousPeriodStart, reportingType);
    };

    const handleCurrentPeriod = (e) => {
        e?.preventDefault?.();
        const currentStart = dayjs()
            .tz(timeZone)
            .startOf(isYearly ? 'year' : 'month')
            .format('YYYY-MM-DD');
        setDataMonth(currentStart);
        syncSearchParams(currentStart, reportingType);
    };

    const handlePeriodTypeChange = (nextType) => {
        const nextPeriodStart = dayjs()
            .tz(timeZone)
            .startOf(nextType === 'yearly' ? 'year' : 'month')
            .format('YYYY-MM-DD');

        setReportingType(nextType);
        setDataMonth(nextPeriodStart);
        setCustomMonth('');
        syncSearchParams(nextPeriodStart, nextType);
    };

    const handleApplyCustomMonth = () => {
        const start = dayjs(customMonth)
            .tz(timeZone)
            .startOf(isYearly ? 'year' : 'month')
            .format('YYYY-MM-DD');
        setDataMonth(start);
        syncSearchParams(start, reportingType);

        const modalEl = document.getElementById('period');
        const modalInstance = window.bootstrap.Modal.getInstance(modalEl);
        modalInstance?.hide();
    };

    return (
        <>
            <Helmet>
                <title>{getPageMeta('reportIncome', language).title}</title>
                <meta
                    name="description"
                    content={getPageMeta('reportIncome', language).description}
                />
                <meta name="keywords" content={getPageMeta('reportIncome', language).keywords} />
                <link rel="canonical" href={getPageMeta('reportIncome', language).url} />
                {getPageMeta('reportIncome', language).noindex && (
                    <meta name="robots" content="noindex, follow" />
                )}

                {/* Open Graph Tags */}
                <meta property="og:type" content="website" />
                <meta property="og:title" content={getPageMeta('reportIncome', language).title} />
                <meta
                    property="og:description"
                    content={getPageMeta('reportIncome', language).description}
                />
                <meta property="og:url" content={getPageMeta('reportIncome', language).url} />
                <meta property="og:image" content={getPageMeta('reportIncome', language).image} />

                {/* Twitter Card Tags */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={getPageMeta('reportIncome', language).title} />
                <meta
                    name="twitter:description"
                    content={getPageMeta('reportIncome', language).description}
                />
                <meta name="twitter:image" content={getPageMeta('reportIncome', language).image} />
            </Helmet>
            <div className="modal add-money-modal fade" id="period" tabIndex="-1">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2 className="modal-title">{t('selectPeriod')}</h2>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label htmlFor="inputfromdate" className="form-label">
                                    {t('fromDate')}
                                </label>
                                <input
                                    type="month"
                                    className="form-control"
                                    id="inputfromdate"
                                    value={customMonth}
                                    onChange={(e) => setCustomMonth(e.target.value)}
                                />
                            </div>

                            <button
                                className="btn theme-btn successfully w-100 mt-4"
                                onClick={handleApplyCustomMonth}
                            >
                                {t('apply')}
                            </button>
                        </div>

                        <button type="button" className="btn close-btn" data-bs-dismiss="modal">
                            <i className="icon" data-feather="x"></i>
                        </button>
                    </div>
                </div>
            </div>

            <header className="section-t-space">
                <div className="custom-container">
                    <div className="header-panel">
                        <BackButton fallback="/reports" />
                        <h2>{t('incomeReport')}</h2>

                        <div className="dropdown">
                            <Link
                                to="#"
                                className="back-btn"
                                role="button"
                                data-bs-toggle="dropdown"
                            >
                                <i className="icon" data-feather="settings"></i>
                            </Link>

                            <ul className="dropdown-menu">
                                <li>
                                    <button
                                        type="button"
                                        className={`dropdown-item${reportingType === 'monthly' ? ' fw-semibold text-primary' : ''}`}
                                        onClick={() => handlePeriodTypeChange('monthly')}
                                    >
                                        {t('monthly')}
                                    </button>
                                </li>
                                <li>
                                    <button
                                        type="button"
                                        className={`dropdown-item${reportingType === 'yearly' ? ' fw-semibold text-primary' : ''}`}
                                        onClick={() => handlePeriodTypeChange('yearly')}
                                    >
                                        {t('yearly')}
                                    </button>
                                </li>
                                <li>
                                    <hr className="dropdown-divider" />
                                </li>
                                <li>
                                    <Link
                                        className="dropdown-item"
                                        to=""
                                        onClick={handleCurrentPeriod}
                                    >
                                        {isYearly ? t('thisYear') : t('thisMonth')}
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        to="#"
                                        onClick={handlePrevPeriod}
                                        className="dropdown-item"
                                    >
                                        {isYearly ? t('lastYear') : t('lastMonth')}
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        className="dropdown-item"
                                        to="#period"
                                        data-bs-toggle="modal"
                                    >
                                        {t('custom')}
                                    </Link>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </header>

            <section>
                <div className="custom-container">
                    {error && <Alert alert={'danger'}>{error}</Alert>}
                    <Loader loading={loading} />

                    <div className="statistics-banner">
                        <div className="d-flex justify-content-center gap-3">
                            <div className="statistics-content">
                                <h5>{chartHeader}</h5>
                                <h3 className="text-white fw-semibold">
                                    {'৳ '}
                                    {amountDisplay(data?.totalIncome, language)}
                                </h3>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <DayWiseLineChart
                expenseData={data?.incomeData ?? []}
                daysInMonth={data?.daysInMonth ?? 0}
                header={chartHeader}
                title={t('income')}
                xAxisLabels={yearChartLabels}
            />

            <CategoryBarChart
                categoryWiseExpense={data?.categoryWiseIncome}
                titleKey="categoryWiseIncome"
                nameKey="income"
                periodLabel={periodLabel}
            />

            <BottomNavbar />
        </>
    );
}

export default ReportIncome;
